import { useMemo } from "react";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { money } from "@/lib/api";

/**
 * RevenueChart — daily total revenue for the workshop.
 *
 * Revenue on a given date = sum of workshop invoices linked to job cards
 * (issued and beyond) + counter sales of spare parts on that date.
 * The server always returns the last 60 days; this component slices
 * the window by the active range filter (today / week / month / custom / all).
 */
export function RevenueChart({ series = [], range = "today", days = 60, loading = false }) {
  const { data, total, jcTotal, csTotal, rangeLabel } = useMemo(() => {
    const src = series || [];
    // Pick how many trailing days to show based on the active header range.
    let take;
    if (range === "today") take = 1;
    else if (range === "week") take = 7;
    else if (range === "month") take = 30;
    else take = src.length || days;
    const sliced = src.slice(-take);
    const t = sliced.reduce((s, r) => s + (Number(r.revenue) || 0), 0);
    const jc = sliced.reduce((s, r) => s + (Number(r.job_card_revenue) || 0), 0);
    const cs = sliced.reduce((s, r) => s + (Number(r.counter_revenue) || 0), 0);
    const withLabel = sliced.map((r) => ({
      ...r,
      label: new Date(r.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    }));
    const label =
      range === "today" ? "Today" :
      range === "week"  ? "This Week" :
      range === "month" ? "This Month" :
      range === "custom" ? "Custom Range" :
      `Last ${take} days`;
    return { data: withLabel, total: t, jcTotal: jc, csTotal: cs, rangeLabel: label };
  }, [series, range, days]);

  const empty = !loading && data.every((r) => (Number(r.revenue) || 0) === 0);

  return (
    <section className="bg-white rounded-xl border border-[#E4E7EC] p-5"
             data-testid="chart-revenue-trend">
      <header className="mb-3 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-[#12B76A]/12 text-[#0F9256] grid place-items-center">
            <TrendingUp size={16} />
          </div>
          <div>
            <h3 className="text-[15px] font-semibold text-[#1d3557]">Total Revenue</h3>
            <p className="text-[11.5px] text-[#667085] mt-0.5">
              {rangeLabel} · workshop invoices + counter sales
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 text-[11.5px] flex-wrap">
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Total</div>
            <div className="text-[18px] font-bold text-[#1d3557] font-mono tabular-nums leading-tight"
                 data-testid="revenue-total">₹{money(total)}</div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Job Cards</div>
            <div className="text-[13px] font-bold text-[#1d3557] font-mono tabular-nums"
                 data-testid="revenue-job-card-total">₹{money(jcTotal)}</div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Counter Sales</div>
            <div className="text-[13px] font-bold text-[#1d3557] font-mono tabular-nums"
                 data-testid="revenue-counter-total">₹{money(csTotal)}</div>
          </div>
        </div>
      </header>

      <div className="h-[220px]" data-testid="revenue-chart-inner">
        {loading ? (
          <div className="h-full grid place-items-center text-[13px] text-[#667085]">
            Loading revenue…
          </div>
        ) : empty ? (
          <div className="h-full grid place-items-center text-center px-4">
            <div>
              <div className="text-[13px] font-semibold text-[#1d3557]">No revenue in this window</div>
              <p className="text-[11.5px] text-[#667085] mt-1 max-w-[360px]">
                Nothing has been billed yet. Once you close job cards or record a
                counter sale, revenue will show up here.
              </p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 10, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#12B76A" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#12B76A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E7EC" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#667085" }}
                     tickLine={false} axisLine={{ stroke: "#E4E7EC" }}
                     minTickGap={data.length > 30 ? 24 : 4} />
              <YAxis tick={{ fontSize: 11, fill: "#667085" }} tickLine={false}
                     axisLine={{ stroke: "#E4E7EC" }} width={54}
                     tickFormatter={(v) => (v >= 100000 ? `₹${(v / 100000).toFixed(1)}L`
                                              : v >= 1000 ? `₹${(v / 1000).toFixed(0)}k`
                                              : `₹${v}`)} />
              <Tooltip
                contentStyle={{ background: "#1d3557", border: "none", borderRadius: 6,
                                color: "white", fontSize: 12, padding: "6px 10px" }}
                itemStyle={{ color: "white" }}
                labelStyle={{ color: "#a8dadc", marginBottom: 2 }}
                formatter={(v, k) => [`₹${money(v)}`,
                  k === "revenue" ? "Revenue" : k === "job_card_revenue" ? "Job Cards" : "Counter Sales"]}
              />
              <Area type="monotone" dataKey="revenue" stroke="none" fill="url(#revenueFill)" />
              <Line type="monotone" dataKey="revenue" stroke="#12B76A" strokeWidth={2.4}
                    dot={data.length <= 14 ? { r: 3, stroke: "#12B76A", fill: "white", strokeWidth: 2 } : false}
                    activeDot={{ r: 5 }} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}
